import { onValue, ref } from "firebase/database";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { database } from "../firebase/firebase";
import {
  EMPTY_PRICING_DATA,
  Extra,
  Place,
  PricingData,
  Route,
  WithId,
  Zone,
  normalizePricingData,
} from "../lib/pricing";

interface PricingContextValue {
  data: PricingData;
  loading: boolean;
  error: boolean;
  /** true quando existe pelo menos uma região cadastrada */
  hasData: boolean;
  zones: WithId<Zone>[];
  places: WithId<Place>[];
  routes: WithId<Route>[];
  extras: WithId<Extra>[];
  zoneNames: Record<string, string>;
}

const PricingContext = createContext<PricingContextValue | undefined>(undefined);

const toList = <T,>(record: Record<string, T>): WithId<T>[] =>
  Object.entries(record).map(([id, value]) => ({ id, ...value }));

const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

/**
 * Carrega `pricing/*` do RTDB. Montar apenas nas rotas que usam a tabela de
 * preços (página pública de orçamento e módulo admin) para evitar leituras
 * desnecessárias no restante do app.
 */
export const PricingProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<PricingData>(EMPTY_PRICING_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const pricingRef = ref(database, "pricing");
    const unsubscribe = onValue(
      pricingRef,
      (snapshot) => {
        setData(normalizePricingData(snapshot.val()));
        setError(false);
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const value = useMemo<PricingContextValue>(() => {
    const zones = toList(data.zones).sort(
      (a, b) => a.order - b.order || collator.compare(a.name, b.name)
    );
    const zoneNames = zones.reduce<Record<string, string>>((acc, zone) => {
      acc[zone.id] = zone.name;
      return acc;
    }, {});
    const places = toList(data.places).sort((a, b) => {
      const zoneA = data.zones[a.zoneId]?.order ?? 999;
      const zoneB = data.zones[b.zoneId]?.order ?? 999;
      return zoneA - zoneB || collator.compare(a.name, b.name);
    });
    const routes = toList(data.routes).sort((a, b) => {
      const destA = data.zones[a.destinationZoneId]?.order ?? 999;
      const destB = data.zones[b.destinationZoneId]?.order ?? 999;
      return destA - destB;
    });
    const extras = toList(data.extras).sort((a, b) => collator.compare(a.name, b.name));

    return {
      data,
      loading,
      error,
      hasData: zones.length > 0,
      zones,
      places,
      routes,
      extras,
      zoneNames,
    };
  }, [data, loading, error]);

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error("usePricing must be used within a PricingProvider");
  }
  return context;
};
